import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MapContainer, TileLayer, Polyline, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import { ChevronLeft, Heart, Download, BookmarkPlus, BookmarkCheck, Map as MapIcon, Star, PawPrint, CloudSun, AlertTriangle, Share2, Droplets, Pencil, MoreVertical, Clipboard, Maximize2, X, Navigation, Satellite, Map, Footprints, Flag, Camera } from "lucide-react";
import { TiwazIcon } from "../components/NorseIcons";
import ExternalLink from "../components/ExternalLink";
import { AMENITY_META, trailAmenityKeys } from "../lib/amenities";
import LiveUserMarker from "../components/LiveUserMarker";
import LocateBtn from "../components/LocateBtn";
import FollowController from "../components/FollowController";
import { useLiveTracking } from "../lib/useLiveTracking";
import { useGroundTemp, classifyPawSafety } from "../components/PawTempBadge";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useUnits, formatDistance } from "../lib/units";
import { getAnonFavs, toggleAnonFav, isOffline, saveOffline, removeOffline, offlineSizeKB, getOffline, downloadGPX, getOfflineRoute } from "../lib/storage";
import { coverImageFor, isFallbackImage } from "../lib/stockImages";
import TransitSection from "../components/TransitSection";
import LivestockSection from "../components/LivestockSection";
import PhotoGallery from "../components/PhotoGallery";
import KnownIssuesSection from "../components/KnownIssuesSection";
import { KnotworkDivider } from "../components/NorseIcons";
import { AchievementBadges, formatAuthorLine } from "../lib/achievements";
import { guestNudge, celebrateAchievement } from "../lib/achievementUX";
import HelpfulButton from "../components/HelpfulButton";
import HideReviewDialog, { HIDE_REASONS } from "../components/HideReviewDialog";
import UserAvatar from "../components/UserAvatar";
import ElevationChart from "../components/ElevationChart";
import DifficultyPill from "../components/DifficultyPill";
import SamplePhotoTag from "../components/SamplePhotoTag";
import { gradientColor } from "../lib/gradient";
import { useWalkRecorder, getResumableWalk, clearResumableWalk } from "../lib/useWalkRecorder";
import { useWakeLock } from "../lib/useWakeLock";
import DogSafetyBanner from "../components/DogSafetyBanner";
import ReportIssueModal from "../components/ReportIssueModal";
import MudMeterSection from "../components/MudMeterSection";
import PawVerifyButton from "../components/PawVerifyButton";
import EditTrailModal from "../components/EditTrailModal";
import WaterSourcesSection from "../components/WaterSourcesSection";
import AuthGateModal from "../components/AuthGateModal";
import ScoutRatingModal from "../components/ScoutRatingModal";
import { track, rememberRegion } from "../lib/telemetry";
import { shareLink } from "../lib/share";
import { useOnlineStatus } from "../lib/useOnlineStatus";

const TIER_STYLE = {
  great: { bg: "bg-brand-primary text-brand-sand", label: "Great walk weather" },
  ok: { bg: "bg-[#D19C4C] text-white", label: "OK conditions" },
  avoid: { bg: "bg-brand-error text-white", label: "Avoid today" },
};

const trailIcon = L.divIcon({
  className: "",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  html: `<div class="td-marker" style="background:#B16B3C;width:22px;height:22px;font-size:11px">●</div>`,
});

// Simplified, user-facing Start / Finish pins for the trail detail mini map.
// Same 🏁 / 🎯 language as the admin picker so a walker who saw the badge
// on Discover recognises the mid-route markers instantly, but sized down
// (26px) and without the pill-shaped label to keep the mini map clean.
const startPin = L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `<div class="td-marker" style="background:#3F5D44;color:#F6EFE0;width:26px;height:26px;font-size:12px">🏁</div>`,
});
const finishPin = L.divIcon({
  className: "",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
  html: `<div class="td-marker" style="background:#B16B3C;color:#F6EFE0;width:26px;height:26px;font-size:12px">🎯</div>`,
});

function FitBounds({ polyline }) {
  const map = useMap();
  useEffect(() => {
    if (polyline && polyline.length > 1) {
      const b = L.latLngBounds(polyline);
      // We added 'animate: false' right here.
      // This stops Leaflet from getting permanently stuck in a camera slide 
      // which was secretly blocking your touch gestures!
      map.fitBounds(b, { padding: [30, 30], animate: false });
    }
  }, [polyline, map]);
  return null;
}

/**
 * Force Leaflet to recompute its container size whenever the fullscreen
 * overlay toggles. Without this the tile grid stays sized for the mini
 * map's 224px height and you end up with a fullscreen area that only
 * paints the top strip — classic Leaflet-in-flex gotcha.
 *
 * Two-frame delay before invalidateSize lets the browser apply the
 * `fixed inset-0` styles first so Leaflet reads the new dimensions
 * correctly on the very first paint of the expanded state.
 */
function InvalidateOnResize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => map.invalidateSize());
    });
    return () => cancelAnimationFrame(raf);
  }, [trigger, map]);
  return null;
}

function ReviewStars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          className={`p-1 ${i <= value ? "text-brand-accent" : "text-brand-border"}`}
          aria-label={`Set ${i}`}
        >
          <Star size={20} fill={i <= value ? "#B16B3C" : "transparent"} />
        </button>
      ))}
    </div>
  );
}

// Consolidated Weather Right Now card — one card owns everything weather-
// and paw-safety related on the trail detail page: air temp, sky
// conditions, wind/humidity, ground temp, hot/cold paw warning, the
// existing dog_safety heatstroke banner, and the 5-day forecast strip.
function WeatherRightNow({ weather, lat, lng }) {
  const { surfaceC } = useGroundTemp(lat, lng);
  const pawTone = classifyPawSafety(surfaceC); // { key, label, Icon, tone }
  const dogSafety = weather?.dog_safety; // may include heatstroke/cold advice from backend
  const cur = weather?.current;
  if (!cur && surfaceC == null && !dogSafety) return null; // nothing to show

  return (
    <section className="td-card p-5 sm:p-6 mt-5 relative overflow-hidden" data-testid="weather-section">
      <div className="absolute -right-12 -bottom-12 w-64 h-64 opacity-5 pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%232C3A2E' stroke-width='2'%3E%3Ccircle cx='50' cy='50' r='40' opacity='0.3'/%3E%3Ccircle cx='50' cy='50' r='30' opacity='0.5'/%3E%3Cpath d='M50 5 L50 95 M5 50 L95 50 M18 18 L82 82 M18 82 L82 18' /%3E%3Cpath d='M45 10 L50 5 L55 10 M85 45 L90 50 L85 55 M55 90 L50 95 L45 90 M15 55 L10 50 L15 45' /%3E%3C/svg%3E\")", backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }} />
      <div className="relative z-10">
      <div className="td-section-label">Weather right now</div>

      {cur && (
        <>
          <div className="flex items-end gap-4 mt-3">
            <div
              className="text-5xl font-heading font-semibold text-brand-text"
              data-testid="weather-air-temp-big"
            >
              {Math.round(cur.temp_c)}°
            </div>
            <div className="text-brand-subtle pb-2 capitalize">{cur.description}</div>
          </div>
          <div className="text-sm text-brand-subtle mt-1" data-testid="weather-metrics">
            Feels {Math.round(cur.feels_like_c)}° · Wind {cur.wind_ms.toFixed(1)} m/s · Humidity {cur.humidity}%
            {typeof surfaceC === "number" && (
              <> · <span data-testid="weather-ground-temp">Ground {Math.round(surfaceC)}°C</span></>
            )}
          </div>
        </>
      )}

      {/* Fallback: if backend weather is offline but we still have a
          ground temp reading, at least show that so walkers still get
          the paw-safety cue. */}
      {!cur && typeof surfaceC === "number" && (
        <div className="mt-3 text-sm text-brand-text" data-testid="weather-ground-only">
          Ground temperature <span className="font-heading text-lg">{Math.round(surfaceC)}°C</span>
        </div>
      )}

      {/* Paw-safety warning box — highlighted only when the ground temp
          crosses the hot-paw (>25°C) or freeze (<0°C) threshold. */}
      {pawTone.key !== "ok" && typeof surfaceC === "number" && (
        <div
          data-testid="paw-safety-warning"
          data-state={pawTone.key}
          className={`mt-4 rounded-2xl px-3.5 py-3 flex items-center gap-3 border ${
            pawTone.tone === "warn"
              ? "bg-amber-50 border-amber-300 text-amber-900"
              : "bg-sky-50 border-sky-300 text-sky-900"
          }`}
        >
          <span className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
            pawTone.tone === "warn" ? "bg-amber-200 text-amber-900" : "bg-sky-200 text-sky-900"
          }`}>
            <pawTone.Icon size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm leading-tight">
              {pawTone.tone === "warn" ? "Hot paws warning" : "Freezing paws warning"}
            </div>
            <div className="text-xs leading-snug mt-0.5">{pawTone.label}</div>
          </div>
        </div>
      )}

      {/* Backend-provided dog safety banner — heatstroke / hydration
          advice. Rendered INSIDE the same card so all weather + paw
          info lives in one place. */}
      {dogSafety && (
        <div className="mt-4" data-testid="dog-safety-inside-weather">
          <DogSafetyBanner safety={dogSafety} />
        </div>
      )}

      {/* 5-day forecast strip */}
      {weather?.forecast?.length > 0 && (
        <div
          className="flex gap-2 mt-4 overflow-x-auto -mx-1 px-1 pb-1 snap-x snap-mandatory"
          data-testid="weather-forecast-row"
        >
          {weather.forecast.map((f) => (
            <div
              key={f.date}
              className="flex flex-col items-center justify-center shrink-0 w-16 py-2 rounded-2xl bg-brand-sand/60 border border-brand-border/60 snap-start text-xs"
            >
              <div className="text-brand-subtle">{new Date(f.date).toLocaleDateString("en-GB", { weekday: "short" })}</div>
              <div className="text-2xl mt-0.5">{getEmoji(f.weather_id)}</div>
              <div className="text-brand-text mt-0.5">{Math.round(f.max_c)}°<span className="text-brand-subtle">/{Math.round(f.min_c)}°</span></div>
            </div>
          ))}
        </div>
      )}
      </div>
    </section>
  );
}


export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { units } = useUnits();
  const online = useOnlineStatus();
  const [tab, setTab] = useState("overview");
  const [fav, setFav] = useState(() => getAnonFavs().includes(id));
  const [offline, setOffline] = useState(() => isOffline(id));
  const [tileCfg, setTileCfg] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [scoutOpen, setScoutOpen] = useState(false);
  // Shared live-tracking hook — same Ozzy-snout marker + rotating cone +
  // Locate Me button used by the main /map. Hook's own useEffect handles
  // GPS + orientation cleanup on unmount, so navigating away from a trail
  // detail view never leaks a background watcher.
  const live = useLiveTracking();
  const [locating, setLocating] = useState(false);
  const [gate, setGate] = useState(null); // {kind:'save'|'gpx'|'walk'} when an auth gate is open
  const [menuOpen, setMenuOpen] = useState(false);
  // Fullscreen mini-map toggle. Uses 100dvh in the CSS class so the map
  // survives mobile browser chrome collapsing/expanding around it — a
  // static 100vh would leave a phantom strip under the URL bar on iOS.
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [isSatellite, setIsSatellite] = useState(false);
  useEffect(() => {
    // Lock the body scroll while the overlay is open — otherwise a two-
    // finger pan on the map bleeds through into the page underneath.
    if (isMapExpanded) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMapExpanded]);

  // Close the kebab dropdown when clicking outside / pressing Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (!e.target.closest("[data-testid='detail-kebab-wrapper']")) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [menuOpen]);

  // review form
  const [r_dog, setRDog] = useState(5);
  const [r_scen, setRScen] = useState(5);
  const [r_fac, setRFac] = useState(4);
  const [r_text, setRText] = useState("");

  const loc = useQuery({
    queryKey: ["loc", id],
    queryFn: async () => {
      // Offline: prefer IndexedDB immediately, no network attempt.
      if (!online) {
        const idb = await getOfflineRoute(id);
        if (idb) {
          return {
            location_id: idb.location_id,
            name: idb.name,
            region: idb.region,
            type: idb.type,
            lat: idb.lat,
            lng: idb.lng,
            polyline: idb.polyline,
            dog_amenities: idb.dog_amenities,
            short_desc: idb.short_desc,
          };
        }
        const legacy = getOffline()[id];
        if (legacy?.location) return legacy.location;
        throw new Error("Offline and no saved copy of this route.");
      }
      try {
        return (await api.get(`/locations/${id}`)).data;
      } catch (e) {
        // Network dropped mid-session: fall back to IndexedDB.
        const idb = await getOfflineRoute(id);
        if (idb) {
          return {
            location_id: idb.location_id, name: idb.name, region: idb.region,
            type: idb.type, lat: idb.lat, lng: idb.lng, polyline: idb.polyline,
            dog_amenities: idb.dog_amenities, short_desc: idb.short_desc,
          };
        }
        const cached = getOffline()[id];
        if (cached?.location) return cached.location;
        throw e;
      }
    },
  });
  const weather = useQuery({
    queryKey: ["loc-weather", id],
    queryFn: async () => {
      if (!online) {
        const idb = await getOfflineRoute(id);
        return idb?.weather_snapshot || getOffline()[id]?.weather || null;
      }
      try {
        return (await api.get(`/weather/location/${id}`)).data;
      } catch {
        const idb = await getOfflineRoute(id);
        return idb?.weather_snapshot || getOffline()[id]?.weather || null;
      }
    },
    enabled: !!loc.data,
  });
  const reviews = useQuery({
    queryKey: ["loc-reviews", id],
    queryFn: async () => (await api.get(`/locations/${id}/reviews`)).data.items,
    enabled: !!loc.data && online,
  });
  // Walk-recorder state — reuses the already-instantiated `live`
  // tracker + follow-lock. Instantiated unconditionally AFTER `loc` is
  // declared so the memo can safely reference `loc.data?.name` without
  // tripping the TDZ (phase-1 regression). All effects/handlers below
  // are callbacks or `useEffect`s — no early-return before this point,
  // so the hook call order is identical every render.
  const trailForRecorder = React.useMemo(
    () => ({ location_id: id, name: loc.data?.name }),
    [id, loc.data?.name],
  );
  const walker = useWalkRecorder({ userPos: live.userPos, trail: trailForRecorder });
  useWakeLock(walker.walking);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [finishedWalk, setFinishedWalk] = useState(null);
  const [submittingWalk, setSubmittingWalk] = useState(false);
  const [resumePromptSeen, setResumePromptSeen] = useState(false);
  const [refreshingFix, setRefreshingFix] = useState(false);
  const [permissionLost, setPermissionLost] = useState(false);
  useEffect(() => {
    if (resumePromptSeen) return;
    const r = getResumableWalk();
    if (r && r.trail_id === id && !walker.walking) {
      setResumePromptSeen(true);
      toast(`Resume your walk on ${r.trail_name || "this trail"}?`, {
        duration: 15000,
        action: {
          label: "Resume",
          onClick: () => { if (!live.tracking) live.startTracking(); },
        },
        cancel: {
          label: "Discard",
          onClick: () => { clearResumableWalk(); window.location.reload(); },
        },
      });
    }
  }, [id, walker.walking, resumePromptSeen, live]);
  useEffect(() => {
    if (!walker.walking) return;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!navigator.geolocation) return;
      setRefreshingFix(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          live.setUserPos({
            position: [pos.coords.latitude, pos.coords.longitude],
            accuracy: pos.coords.accuracy,
          });
          setRefreshingFix(false);
          setPermissionLost(false);
        },
        (err) => {
          setRefreshingFix(false);
          if (err && err.code === err.PERMISSION_DENIED) setPermissionLost(true);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
      );
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [walker.walking, live]);
  const startWalk = () => {
    if (!user) {
      setGate({ kind: "walk" });
      return;
    }
    if (!live.tracking) live.startTracking();
    live.setIsFollowing(true);
    walker.start();
    toast.success("Walk started");
  };
  const submitWalk = async () => {
    setSubmittingWalk(true);
    try {
      const payload = walker.finishPayload();
      const r = await api.post("/walks", payload);
      walker.complete();
      setConfirmFinish(false);
      setFinishedWalk({ ...r.data, trail_name: loc.data?.name || "" });
      live.stopTracking();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save walk");
    } finally {
      setSubmittingWalk(false);
    }
  };
  const cancelWalk = () => {
    walker.cancel();
    setConfirmCancel(false);
    toast("Walk discarded");
  };

  useEffect(() => {
    api.get("/map/os-config").then((r) => setTileCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loc.data) {
      track("location_view", { id, name: loc.data.name, type: loc.data.type }, loc.data.region);
      // Persist so downstream bare events (discover_view/map_view) inherit
      // the region on the admin heatmap.
      if (loc.data.region) rememberRegion(loc.data.region);
    }
  }, [id, loc.data]);

  useEffect(() => {
    const handler = () => setOffline(isOffline(id));
    window.addEventListener("td-offline-changed", handler);
    return () => window.removeEventListener("td-offline-changed", handler);
  }, [id]);

  // Refetch location + weather from IDB/network when connectivity flips.
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ["loc", id] });
    qc.invalidateQueries({ queryKey: ["loc-weather", id] });
  }, [online, id, qc]);

  // Anonymous-only fallback for the "save just on this device" path.
  const persistAnonFav = () => {
    const next = toggleAnonFav(id);
    setFav(next);
  };

  const toggleFav = async () => {
    // Logged-out: nudge them to create a free account first, but offer a
    // graceful "save just on this device" escape so they can still keep
    // the trail without forcing a sign-in.
    if (!user) {
      if (fav) {
        // Removing a previously-saved walk doesn't need a gate.
        persistAnonFav();
        return;
      }
      track("auth_gate_shown", { reason: "save_walk", trail_id: id });
      setGate({ kind: "save" });
      return;
    }
    try {
      if (fav) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
    } catch {}
    persistAnonFav();
  };

  const toggleOffline = async () => {
    if (offline) {
      removeOffline(id);
      toast.success("Removed from offline library");
      return;
    }
    saveOffline(id, {
      location: loc.data,
      weather: weather.data,
      polyline: loc.data?.polyline,
    });
    toast.success("Saved for offline");
  };

  const onExportGPX = () => {
    if (!loc.data) return;
    // GPX export is a "real download" — gate it behind sign-in. No guest
    // fallback here (per acquisition-flow brief).
    if (!user) {
      track("auth_gate_shown", { reason: "export_gpx", trail_id: id });
      setGate({ kind: "gpx" });
      return;
    }
    try {
      downloadGPX(loc.data, loc.data.polyline);
      toast.success("GPX downloaded");
    } catch (e) {
      console.error("GPX export failed", e);
      toast.error("Couldn't export GPX");
    }
  };

  const shareTrail = () => {
    if (!loc.data) return;
    shareLink({
      url: `${window.location.origin}/api/og/location/${loc.data.location_id}`,
      title: "Check out this trail on TrailDog!",
      text: `I just found this great dog-friendly trail on TrailDog: ${loc.data.name}. Check it out here!`,
      event: "share_trail",
      props: { trail_id: loc.data.location_id },
    });
  };

  const [submittingReview, setSubmittingReview] = React.useState(false);
  const [hideTarget, setHideTarget] = React.useState(null);
  const submitReview = async (ev) => {
    if (guestNudge(user, ev)) return;
    if (submittingReview) return;
    setSubmittingReview(true);
    try {
      const res = await api.post(`/locations/${id}/reviews`, {
        dog_friendliness: r_dog,
        scenery: r_scen,
        facilities: r_fac,
        comment: r_text,
      });
      setRText("");
      toast.success(
        res?.data?.message ||
        (res?.data?.updated ? "Review updated" : "Review posted")
      );
      qc.invalidateQueries({ queryKey: ["loc-reviews", id] });
      celebrateAchievement(navigate, res?.data?.new_achievement);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't post review";
      toast.error(msg);
    } finally {
      setSubmittingReview(false);
    }
  };

const polylineLatLng = React.useMemo(() => {
    const poly = loc.data?.polyline || [];
    return poly.map(([lng, lat]) => [lat, lng]);
  }, [loc.data?.polyline]);
  // Elevation scrubber state — index onto routeLatLng used to drop a
  // marker on the map at the walker's scrub position. Null when the
  // walker hasn't touched the chart yet (hides the marker).
  const [scrubPt, setScrubPt] = React.useState(null);
  const elevationProfile = loc.data?.elevation_profile || [];
  // Build coloured segments for the Leaflet route. Each segment ends on
  // a polyline-coordinate boundary mapped from the elevation downsample.
  const coloredSegments = React.useMemo(() => {
    if (!elevationProfile.length || polylineLatLng.length < 2) return null;
    const n = elevationProfile.length;
    const polyN = polylineLatLng.length;
    // Map each elevation point to a polyline index.
    const toPolyIdx = (i) => Math.min(polyN - 1, Math.round((i / (n - 1)) * (polyN - 1)));
    const segs = [];
    for (let i = 1; i < n; i++) {
      const dKm = elevationProfile[i][0] - elevationProfile[i - 1][0];
      const dM = elevationProfile[i][1] - elevationProfile[i - 1][1];
      const grad = dKm > 0 ? dM / (dKm * 1000) : 0;
      const a = toPolyIdx(i - 1);
      const b = toPolyIdx(i);
      if (b <= a) continue;
      segs.push({ positions: polylineLatLng.slice(a, b + 1), color: gradientColor(grad) });
    }
    return segs;
  }, [elevationProfile, polylineLatLng]);
  if (loc.isLoading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!loc.data) {
    if (!online) {
      return (
        <div className="p-8 text-brand-subtle" data-testid="location-offline-empty">
          You&apos;re offline and this walk hasn&apos;t been saved for offline use. Reconnect to view it, or save it for offline next time you have signal.
        </div>
      );
    }
    return <div className="p-8 text-brand-subtle">Location not found.</div>;
  }

  const L0 = loc.data;
  
  const tier = weather.data?.walk_score?.tier;
  const tierStyle = TIER_STYLE[tier];
  // Fallback image state is derived from the location doc, declared here
  // before the render so the hero tag + "Add yours" link are driven off
  // a single source of truth.
  const heroIsFallback = isFallbackImage(L0);
  const scrollToPhotoUpload = () => {
    const anchor = document.querySelector('[data-testid="photo-gallery-anchor"]');
    anchor?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      document.querySelector('[data-testid="upload-photo-btn"]')?.focus?.();
    }, 400);
  };

  return (
    <div className="pb-16">
      {/* Hero */}
      <div className="relative">
        <div className="relative aspect-[16/10] sm:aspect-[16/8] bg-brand-border/30 overflow-hidden">
          <img src={coverImageFor(L0)} alt="" className="w-full h-full object-cover" />
          {heroIsFallback && <SamplePhotoTag testId="sample-photo-tag-hero" />}
        </div>
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-brand-surface/90 backdrop-blur border border-brand-border flex items-center justify-center"
            aria-label="Back"
          >
            <ChevronLeft size={20} className="text-brand-text" />
          </button>
          <div className="flex gap-2">
            <button
              data-testid="fav-toggle-detail"
              onClick={toggleFav}
              className="w-10 h-10 rounded-full bg-brand-surface/90 backdrop-blur border border-brand-border flex items-center justify-center"
              aria-label="Save to My Diary"
              title="Save to My Diary"
            >
              <Heart size={18} className={fav ? "fill-brand-accent text-brand-accent" : "text-brand-text"} />
            </button>
            <button
              data-testid="offline-toggle-detail"
              onClick={toggleOffline}
              className="w-10 h-10 rounded-full bg-brand-surface/90 backdrop-blur border border-brand-border flex items-center justify-center"
              aria-label="Save offline"
            >
              {offline ? <BookmarkCheck size={18} className="text-brand-primary" /> : <BookmarkPlus size={18} className="text-brand-text" />}
            </button>
            <div className="relative" data-testid="detail-kebab-wrapper">
              <button
                data-testid="detail-kebab-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="w-10 h-10 rounded-full bg-brand-surface/90 backdrop-blur border border-brand-border flex items-center justify-center"
                aria-label="More actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <MoreVertical size={18} className="text-brand-text" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 rounded-2xl bg-brand-surface border border-brand-border shadow-2xl overflow-hidden z-[1100]"
                  data-testid="detail-kebab-menu"
                >
                  <button
                    role="menuitem"
                    data-testid="menu-export-gpx"
                    onClick={() => { setMenuOpen(false); onExportGPX(); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-brand-text hover:bg-brand-sand text-left"
                  >
                    <Download size={15} className="text-brand-subtle" /> Export GPX
                  </button>
                  <button
                    role="menuitem"
                    data-testid="menu-copy-card"
                    onClick={async () => {
                      setMenuOpen(false);
                      const url = `${window.location.origin}/location/${L0.location_id}`;
                      const cur = weather.data?.current;
                      const wxSegment = cur
                        ? `${getEmoji(cur.weather_id)} ${Math.round(cur.temp_c)}° · ${cur.description} | `
                        : "";
                      const text = `🐾 ${L0.name} - ${L0.region || "United Kingdom"} | ${wxSegment}Dog-friendly walk. View route: ${url}`;
                      try {
                        await navigator.clipboard.writeText(text);
                        toast.success("Walk card copied — paste into WhatsApp or Slack");
                        track("copy_walk_card", { trail_id: L0.location_id, has_weather: !!cur });
                      } catch {
                        toast.error("Couldn't copy — try the Share button instead");
                      }
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-brand-text hover:bg-brand-sand text-left"
                  >
                    <Clipboard size={15} className="text-brand-subtle" /> Copy walk card
                  </button>
                  <button
                    role="menuitem"
                    data-testid="menu-report-issue"
                    onClick={(ev) => { setMenuOpen(false); if (guestNudge(user, ev)) return; setReportOpen(true); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-brand-accent hover:bg-brand-sand text-left"
                  >
                    <AlertTriangle size={15} /> Report issue
                  </button>
                  {user?.is_admin && (
                    <button
                      role="menuitem"
                      data-testid="menu-edit-trail"
                      onClick={() => { setMenuOpen(false); setEditOpen(true); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-brand-primary hover:bg-brand-sand text-left border-t border-brand-border/60"
                    >
                      <Pencil size={15} /> Edit trail
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 -mt-8 relative">
        <div className="td-card p-5 sm:p-6">
          <div className="td-section-label">{L0.type === "camping" ? "Campsite" : "Hiking trail"}</div>
          <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text leading-tight">{L0.name}</h1>
            <PawVerifyButton trailId={id} />
          </div>
          <div className="text-brand-subtle text-sm mt-1">{L0.region}</div>
          {L0.short_desc && <p className="text-brand-subtle mt-3 leading-relaxed">{L0.short_desc}</p>}
          {heroIsFallback && (
            <button
              type="button"
              data-testid="add-your-photo-link"
              onClick={scrollToPhotoUpload}
              className="mt-2 text-xs text-brand-subtle hover:text-brand-accent underline underline-offset-2 decoration-dotted"
            >
              Sample photo — add yours
            </button>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {tierStyle && (
              <div className={`td-chip ${tierStyle.bg} border-transparent`} data-testid="walk-score-chip">
                <CloudSun size={13} /> {tierStyle.label} {weather.data?.walk_score?.emoji}
              </div>
            )}
            {L0.dog_friendly_rating === "accessible" && (
              <div className="td-chip bg-brand-primary text-brand-sand border-brand-primary" data-testid="dog-friendly-chip">
                <PawPrint size={13} /> Dog-friendly route
              </div>
            )}
            {L0.dog_friendly_rating === "difficult" && (
              <div className="td-chip bg-[#D19C4C] text-white border-[#D19C4C]" data-testid="dog-friendly-chip">
                <PawPrint size={13} /> Stiles on route
              </div>
            )}
            {L0.dog_friendly_rating === "restricted" && (
              <div className="td-chip bg-brand-error text-white border-brand-error" data-testid="dog-friendly-chip">
                Dogs not permitted
              </div>
            )}
            {L0.ozzy_verified && (
              <div
                data-testid="ozzy-verified-badge"
                className="td-chip bg-brand-accent text-white border-brand-accent"
                title="Personally walked & verified by Ozzy"
              >
                <PawPrint size={13} className="fill-white" /> Ozzy Verified
              </div>
            )}
            {L0.editor_pick && !L0.ozzy_verified && <div className="td-chip bg-brand-accent text-white border-brand-accent">Editor&apos;s pick</div>}
            {L0.editor_pick && L0.ozzy_verified && <div className="td-chip bg-brand-accent/15 text-brand-accent border-brand-accent/40">Editor&apos;s pick</div>}
            {L0.water_source_available && (
              <div
                data-testid="water-source-badge"
                className="td-chip bg-[#E6F2FB] text-[#1E5B91] border-[#9FCDEC]"
                title="Water source available along this trail"
              >
                <Droplets size={13} className="text-[#2E7AB8]" /> Water source
              </div>
            )}
            {L0.dog_amenities?.slice(0, 4).map((a) => (
              <div key={a} className="td-chip"><PawPrint size={12} /> {a.replace(/_/g, " ")}</div>
            ))}
            {/* Typed amenity badges — derived from the trail's own
                `dog_amenities` tag array via the shared alias mapping so
                walkers see exactly which of the filterable amenities
                (Shaded / Water Tap / Picnic Area) this trail satisfies. */}
            {trailAmenityKeys(L0).map((k) => {
              const { label, Icon } = AMENITY_META[k];
              return (
                <div
                  key={`amenity-${k}`}
                  data-testid={`amenity-badge-${k}`}
                  className="td-chip bg-brand-primary/10 text-brand-primary border-brand-primary/30"
                  title={`${label} amenity available on this trail`}
                >
                  <Icon size={13} /> {label}
                </div>
              );
            })}
          </div>

          {L0.dog_friendly_reasons?.length > 0 && (
            <ul className="mt-3 text-sm text-brand-subtle space-y-1" data-testid="dog-friendly-reasons">
              {L0.dog_friendly_reasons.slice(0, 3).map((r, i) => (
                <li key={i}>· {r}</li>
              ))}
            </ul>
          )}

          {/* Scout CTA — only shown for Scout Needed trails. Prominent gold
              band matching the badge on Discover so walkers immediately
              recognise "this is the same thing, and I can help fix it". */}
          {L0.needsScout && (
            <div
              data-testid="scout-cta-banner"
              className="mt-5 p-4 rounded-2xl bg-[#F5B841]/20 border border-[#F5B841] flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#F5B841] flex items-center justify-center shrink-0">
                <span className="font-bold text-[#2C3A2E] text-lg">🎯</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base text-brand-text leading-tight">
                  This trail needs a scout
                </div>
                <p className="text-xs text-brand-subtle mt-0.5">
                  No difficulty rating yet — help other walkers by scoring it.
                </p>
              </div>
              <button
                data-testid="scout-cta-btn"
                onClick={() => setScoutOpen(true)}
                className="px-4 py-2 rounded-full bg-[#2C3A2E] text-[#F5B841] text-sm font-semibold whitespace-nowrap shrink-0 hover:bg-brand-primary transition-colors"
              >
                Rate this trail
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-5">
            {(() => {
              // Zero-cost car park directions — hand off to Google Maps' universal
              // directions URL. Prefer the explicit start_location (trailhead)
              // over the doc's top-level lat/lng so walkers are routed to the
              // actual walk start, not the trail's centroid.
              const dLat = L0.start_location?.lat ?? L0.lat;
              const dLng = L0.start_location?.lng ?? L0.lng;
              if (typeof dLat !== "number" || typeof dLng !== "number") return null;
              const label = encodeURIComponent(L0.name || "Trail start");
              const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${dLat},${dLng}&destination_place_id=&travelmode=driving&dir_action=navigate&query=${label}`;
              return (
                <ExternalLink
                  href={dirUrl}
                  testid="get-directions-btn"
                  onClick={() => {
                    track("engagement_get_directions", { trail_id: L0.location_id });
                    window.dispatchEvent(new CustomEvent("td-engagement", { detail: { source: "get_directions" } }));
                  }}
                  className="td-btn-primary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
                >
                  <TiwazIcon size={18} /> Get directions to start
                </ExternalLink>
              );
            })()}
            <Link
              data-testid="open-in-map-btn"
              to={`/map?focus=${L0.location_id}`}
              onClick={() => {
                track("engagement_open_on_map", { trail_id: L0.location_id });
                window.dispatchEvent(new CustomEvent("td-engagement", { detail: { source: "open_on_map" } }));
              }}
              className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              <MapIcon size={15} /> Open on map
            </Link>
            <button
              type="button"
              data-testid="share-trail-btn"
              onClick={shareTrail}
              className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              <Share2 size={15} /> Share trail
            </button>
            {offline && (
              <div className="td-chip basis-full sm:basis-auto justify-center">{offlineSizeKB(id)} KB cached</div>
            )}
          </div>
        </div>

        {/* Walk controls — sits above the mini map. Reuses the existing
            live-tracking + follow-lock stack; no new GPS watch. */}
        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="walk-controls">
          {!walker.walking ? (
            <button
              type="button"
              data-testid="start-walk-btn"
              onClick={startWalk}
              className="td-btn-primary !py-2 !px-4 !text-sm inline-flex items-center gap-1.5"
            >
              <Footprints size={15} /> Start walk
            </button>
          ) : (
            <>
              <button
                type="button"
                data-testid="finish-walk-btn"
                onClick={() => setConfirmFinish(true)}
                className="td-btn-primary !py-2 !px-4 !text-sm inline-flex items-center gap-1.5"
              >
                <Flag size={15} /> Finish walk
              </button>
              <button
                type="button"
                data-testid="cancel-walk-btn"
                onClick={() => setConfirmCancel(true)}
                className="td-btn-secondary !py-2 !px-3 !text-sm inline-flex items-center gap-1.5"
              >
                <X size={15} /> Cancel
              </button>
              <div className="text-xs text-brand-subtle flex items-center gap-1.5" data-testid="walk-live-stats">
                {refreshingFix && <span className="italic">Finding you…</span>}
                {permissionLost && (
                  <button
                    data-testid="walk-reenable-location"
                    onClick={() => navigator.geolocation?.getCurrentPosition(() => setPermissionLost(false), () => {})}
                    className="text-brand-error underline"
                  >
                    Tap to re-enable location
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        {walker.walking && (
          <div
            data-testid="walk-banner"
            className="mt-2 px-3 py-1.5 rounded-full bg-brand-sand border border-brand-border text-xs text-brand-text inline-flex items-center gap-2"
          >
            Walk in progress — keep the screen on for best tracking.
          </div>
        )}

        {/* Mini map with external-map handoff overlay (Google / OS / OSM) */}
        {(() => {
          // Explicit start & finish for hiking trails — never derive
          // either from polyline bounds. Falls back to the doc's top-
          // level lat/lng when the backend hasn't populated the
          // sub-docs yet (older imports / non-hiking types).
          const isTrail = L0.type === "hiking";
          const startLat = L0.start_location?.lat ?? L0.lat;
          const startLng = L0.start_location?.lng ?? L0.lng;
          const finishLat = L0.finish_location?.lat ?? null;
          const finishLng = L0.finish_location?.lng ?? null;
          const hasFinish =
            isTrail
            && finishLat !== null
            && finishLng !== null
            // If start and finish are effectively the same coordinate
            // (single-point GPX, or an old row where finish == start)
            // suppress the finish pin so we don't render two markers on
            // top of each other.
            && (Math.abs(finishLat - startLat) > 1e-5 || Math.abs(finishLng - startLng) > 1e-5);
          // The bottom-right "open in Google/OS/OSM" overlay uses the
          // start pin as the primary handoff target — that's the walker's
          // real trailhead.
          const headLat = startLat;
          const headLng = startLng;
          return (
            <div
              className={
                isMapExpanded
                  ? "fixed inset-0 z-[1100] w-full h-[100dvh] bg-white flex flex-col"
                  : "td-card overflow-hidden mt-5 h-56 relative"
              }
              data-testid="mini-map"
            >
              <MapContainer
                center={[startLat, startLng]}
                zoom={13}
                minZoom={7}
                maxZoom={16}
                scrollWheelZoom={isMapExpanded}
                style={{ height: "100%", width: "100%" }}
              >
                {!isSatellite && tileCfg && (
                  <TileLayer
                    url={tileCfg.tile_url_3857 || tileCfg.tile_url || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
                    attribution={tileCfg.attribution}
                    minZoom={7}
                    maxZoom={16}
                    maxNativeZoom={16}
                  />
                )}
                {isSatellite && (
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                    minZoom={7}
                    maxZoom={19}
                    maxNativeZoom={19}
                  />
                )}
                {polylineLatLng.length > 1 && (
                  coloredSegments && coloredSegments.length > 0 ? (
                    coloredSegments.map((seg, i) => (
                      <Polyline
                        key={`seg-${i}`}
                        positions={seg.positions}
                        pathOptions={{ color: seg.color, weight: 4 }}
                      />
                    ))
                  ) : (
                    <Polyline positions={polylineLatLng} pathOptions={{ color: "#B16B3C", weight: 4 }} />
                  )
                )}
                {scrubPt && (
                  <Marker
                    position={[scrubPt.lat, scrubPt.lng]}
                    icon={L.divIcon({
                      className: "",
                      html: '<div style="width:16px;height:16px;border-radius:50%;background:#F6EFE0;border:3px solid #2C3A2E;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>',
                      iconSize: [16, 16],
                      iconAnchor: [8, 8],
                    })}
                    data-testid="elev-scrubber-map-marker"
                  />
                )}
                {isTrail ? (
                  <>
                    <Marker
                      data-testid="trail-start-marker"
                      position={[startLat, startLng]}
                      icon={startPin}
                      eventHandlers={{ click: () => navigate(`/map?focus=${L0.location_id}`) }}
                    />
                    {hasFinish && (
                      <Marker
                        data-testid="trail-finish-marker"
                        position={[finishLat, finishLng]}
                        icon={finishPin}
                        eventHandlers={{ click: () => navigate(`/map?focus=${L0.location_id}`) }}
                      />
                    )}
                  </>
                ) : (
                  <Marker
                    data-testid="trailhead-marker"
                    position={[startLat, startLng]}
                    icon={trailIcon}
                    eventHandlers={{ click: () => navigate(`/map?focus=${L0.location_id}`) }}
                  />
                )}
                <FitBounds polyline={polylineLatLng} />
                <InvalidateOnResize trigger={isMapExpanded} />
             
{/* Live tracking — shows your blue dot and accuracy circle, but NEVER moves the camera */}
{live.userPos && (
  <>
    <Circle
      center={live.userPos.position}
      radius={Math.max(40, Math.min(live.userPos.accuracy || 60, 400))}
      pathOptions={{ color: "#2E7AB8", weight: 1, fillColor: "#3B8CD3", fillOpacity: 0.12 }}
    />
    <LiveUserMarker userPos={live.userPos} heading={live.heading} />
  </>
)}
<LocateBtn
                position="bottom-left"
                onLocated={(p) => {
                  if (p) {
                    // Handled cleanly without locking
                  }
                }}
                tracking={live.tracking}
                onStartTracking={(opts) => {
                  live.startTracking(opts);
                }}
                busy={locating}
                setBusy={setLocating}
                isFollowing={live.isFollowing}
                setIsFollowing={live.setIsFollowing}
              />
              <FollowController userPos={live.userPos} isFollowing={live.isFollowing} setIsFollowing={live.setIsFollowing} />
                
              </MapContainer>

              {/* Expand toggle — small square button in the top-right of
                  the mini map. Swaps to a prominent "Close Map" pill
                  when the overlay is open, with a safe-area-inset top
                  offset so it clears iOS notches and Android status
                  bars on any orientation. */}
              {isMapExpanded ? (
                <button
                  type="button"
                  data-testid="map-close-btn"
                  onClick={() => setIsMapExpanded(false)}
                  className="absolute right-4 z-[700] flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-text text-brand-sand text-sm font-semibold shadow-lg hover:bg-brand-primary transition-colors"
                  style={{ top: "max(1rem, env(safe-area-inset-top))" }}
                >
                  <X size={16} /> Close Map
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="map-expand-btn"
                  onClick={() => setIsMapExpanded(true)}
                  className="absolute top-3 right-3 z-[600] w-9 h-9 rounded-full bg-brand-surface/95 backdrop-blur border border-brand-border shadow-md flex items-center justify-center text-brand-text hover:bg-brand-sand transition-colors"
                  title="Expand map"
                  aria-label="Expand map"
                >
                  <Maximize2 size={16} />
                </button>
              )}

              {/* Satellite / map toggle — mirrors the chip on MapPage.jsx.
                  Placed top-left of the map card so it doesn't clash with
                  the top-right expand/close controls or bottom-right
                  external-maps overlay. */}
              <button
                type="button"
                data-testid="map-chip-satellite"
                onClick={() => setIsSatellite((x) => !x)}
                className={`absolute top-3 left-14 z-[600] td-chip ${isSatellite ? "td-chip-active" : ""} !py-1 !px-2.5`}
                title="Toggle high-res satellite imagery"
              >
                {isSatellite ? <Map size={13} /> : <Satellite size={13} />} {isSatellite ? "Map View" : "Satellite"}
              </button>

              {/* External map handoff — overlay in bottom-right corner */}
              <div
                className="absolute bottom-3 right-3 z-[600] flex gap-1.5 bg-brand-surface/95 backdrop-blur rounded-full border border-brand-border shadow-lg p-1"
                data-testid="external-maps-overlay"
                title={`Open ${isTrail ? "start" : "spot"} in…`}
              >
                <ExternalLink
                  testid="open-google-maps"
                  href={`https://www.google.com/maps/search/?api=1&query=${headLat},${headLng}`}
                  className="w-8 h-8 rounded-full bg-[#4285F4] text-white text-[11px] font-bold flex items-center justify-center hover:scale-110 transition-transform"
                >
                  G
                </ExternalLink>
                <ExternalLink
                  testid="open-os-maps"
                  href={`https://explore.osmaps.com/pin?lat=${headLat}&lon=${headLng}&zoom=15`}
                  className="w-8 h-8 rounded-full bg-brand-primary text-white text-[10px] font-bold flex items-center justify-center hover:scale-110 transition-transform"
                >
                  OS
                </ExternalLink>
                <ExternalLink
                  testid="open-osm"
                  href={`https://www.openstreetmap.org/?mlat=${headLat}&mlon=${headLng}#map=15/${headLat}/${headLng}`}
                  className="w-8 h-8 rounded-full bg-[#7EBC6F] text-white text-[11px] font-bold flex items-center justify-center hover:scale-110 transition-transform"
                >
                  M
                </ExternalLink>
              </div>
            </div>
          );
        })()}

        {/* Elevation profile — rendered ONLY when the GPX had <ele> on
            every point. Hidden entirely for trails without elevation per
            spec. Sits directly under the map so climb + difficulty are
            visible before transit / water / photos. */}
        {elevationProfile.length > 1 && (
          <section className="mt-4 px-1" data-testid="elevation-section">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-xl text-brand-text">Elevation</h3>
              <DifficultyPill difficulty={L0.difficulty} size="md" testId="trail-difficulty-pill" />
            </div>
            <div className="td-card p-4">
              <ElevationChart
                points={elevationProfile}
                routeLatLng={polylineLatLng}
                totalDistanceKm={L0.distance_km}
                onScrub={(s) => setScrubPt(s.lat ? { lat: s.lat, lng: s.lng } : null)}
              />
              <div className="mt-3 pt-3 border-t border-brand-border/50 flex items-center gap-5 text-sm" data-testid="elevation-summary-strip">
                <div>
                  <div className="text-xs text-brand-subtle uppercase tracking-wider">Climb</div>
                  <div className="font-heading text-lg text-brand-text" data-testid="trail-ascent">↑ {L0.ascent_m ?? 0} m</div>
                </div>
                <div>
                  <div className="text-xs text-brand-subtle uppercase tracking-wider">Descent</div>
                  <div className="font-heading text-lg text-brand-text" data-testid="trail-descent">↓ {L0.descent_m ?? 0} m</div>
                </div>
                {L0.max_ele != null && L0.min_ele != null && (
                  <div>
                    <div className="text-xs text-brand-subtle uppercase tracking-wider">Range</div>
                    <div className="font-heading text-lg text-brand-text">{Math.round(L0.min_ele)} – {Math.round(L0.max_ele)} m</div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Transit — "how do I get there" sits right under the elevation
            summary so walkers see climb + travel before amenities. */}
        <TransitSection locationId={id} />

        {/* Water sources — community-curated taps/troughs/streams */}
        <WaterSourcesSection locationId={id} />

        {/* Photo gallery */}
        <div data-testid="photo-gallery-anchor">
          <PhotoGallery locationId={id} name={L0.name} />
        </div>

        <KnotworkDivider />

        {/* Known Issues — active hazard/issue reports from other walkers.
            Sits between the community photo gallery and the Mud Meter so
            walkers see safety info before planning conditions. */}
        <KnownIssuesSection trailId={id} />

        {/* Mud Meter — real-time community conditions */}
        <div data-testid="mud-meter-anchor">
          <MudMeterSection trailId={id} />
        </div>

        <KnotworkDivider />

        {/* Consolidated Weather + Paw Safety card — merges the backend
            /weather/location payload (air temp, description, feels, wind,
            humidity, 5-day forecast) with client-side Open-Meteo ground
            temperature and the dog_safety heatstroke/cold banner so every
            weather + paw signal lives in one card. */}
        <WeatherRightNow
          weather={weather.data}
          lat={L0.start_location?.lat ?? L0.lat}
          lng={L0.start_location?.lng ?? L0.lng}
        />

        {/* Livestock */}
        <LivestockSection locationId={id} />

        {/* Reviews */}
        <section className="td-card p-5 sm:p-6 mt-5" data-testid="reviews-section">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-medium">Walker reviews</h3>
            <div className="text-xs text-brand-subtle">
              {(reviews.data || []).filter((r) => !r.hidden).length} total
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {(reviews.data || []).slice(0, 5).map((rv) => {
              const hiddenForAuthor = rv.hidden && user && rv.user_id === user.user_id;
              const reasonLabel = (HIDE_REASONS.find((r) => r.key === rv.hidden_reason) || {}).label || "No reason given";
              if (rv.hidden && !hiddenForAuthor) return null;
              return (
              <div
                key={rv.review_id}
                className={`border-t border-brand-border/50 pt-3 first:border-t-0 first:pt-0 ${hiddenForAuthor ? "opacity-60" : ""}`}
                data-testid={`review-row-${rv.review_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm flex items-center gap-2" data-testid={`review-author-${rv.review_id}`}>
                    <UserAvatar
                      user={{
                        name: rv.user_name,
                        avatar_url: rv.author_avatar_url,
                        avatar_updated_at: rv.author_avatar_updated_at,
                        picture: rv.author_picture,
                      }}
                      size={32}
                      testId={`review-avatar-${rv.review_id}`}
                    />
                    <span>{formatAuthorLine(rv.user_name, rv.author_dog_name, "A walker")}</span>
                    <AchievementBadges keys={rv.author_achievements} size={13} />
                    {rv.admin_approved && (
                      <span
                        data-testid={`ozzy-badge-${rv.review_id}`}
                        title="Ozzy approved by TrailDog admin"
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-200 to-yellow-100 text-amber-900 border border-amber-300"
                      >
                        🐾 Ozzy approved
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 text-xs text-brand-subtle">
                    <span title="Dog friendliness">🐾 {rv.dog_friendliness}</span>
                    {rv.scenery != null && <span title="Scenery">🌄 {rv.scenery}</span>}
                    {rv.facilities != null && <span title="Facilities">🚰 {rv.facilities}</span>}
                  </div>
                </div>
                {rv.comment && <div className="text-sm text-brand-subtle mt-1">{rv.comment}</div>}
                {hiddenForAuthor && (
                  <div
                    data-testid={`review-hidden-notice-${rv.review_id}`}
                    className="mt-2 text-xs px-2 py-1.5 rounded bg-brand-error/10 text-brand-error"
                  >
                    Hidden by an admin: {reasonLabel}
                    {rv.hidden_note ? ` — ${rv.hidden_note}` : ""}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <HelpfulButton
                    reviewId={rv.review_id}
                    reviewAuthorId={rv.user_id}
                    locationId={id}
                    initialCount={rv.helpful_votes_count || 0}
                    initialVoted={!!rv.viewer_has_voted}
                    currentUser={user}
                  />
                  {user?.is_admin && !rv.hidden && (
                    <button
                      data-testid={`ozzy-toggle-${rv.review_id}`}
                      onClick={async () => {
                        try {
                          await api.post(
                            `/locations/${id}/reviews/${rv.review_id}/ozzy-approve`,
                            { approved: !rv.admin_approved },
                          );
                          qc.invalidateQueries({ queryKey: ["loc-reviews", id] });
                          toast.success(!rv.admin_approved ? "Ozzy approved" : "Approval cleared");
                        } catch {
                          toast.error("Couldn't update approval");
                        }
                      }}
                      className={`text-xs px-2 py-1 rounded-full border ${rv.admin_approved ? "border-amber-400 bg-amber-50 text-amber-800" : "border-brand-border text-brand-subtle hover:text-brand-text"}`}
                      title={rv.admin_approved ? "Clear Ozzy approval" : "Mark as Ozzy approved"}
                    >
                      🐾 {rv.admin_approved ? "Approved" : "Approve"}
                    </button>
                  )}
                  {user?.is_admin && !rv.hidden && (
                    <button
                      data-testid={`hide-review-btn-${rv.review_id}`}
                      onClick={() => setHideTarget(rv)}
                      className="text-xs px-2 py-1 rounded-full border border-brand-error/40 text-brand-error hover:bg-brand-error/5"
                      title="Hide this review"
                    >
                      Hide
                    </button>
                  )}
                </div>
              </div>
            );})}
            {(!reviews.data || reviews.data.length === 0) && (
              <div className="text-sm text-brand-subtle">Be the first to leave a dog-first review.</div>
            )}
          </div>
          <div className="mt-5 border-t border-brand-border pt-4">
            <div className="td-section-label">Leave a review</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
              <div>
                <div className="text-brand-subtle mb-1">Dog friendliness</div>
                <ReviewStars value={r_dog} onChange={setRDog} testid="stars-dog" />
              </div>
              <div>
                <div className="text-brand-subtle mb-1">Scenery</div>
                <ReviewStars value={r_scen} onChange={setRScen} testid="stars-scenery" />
              </div>
              <div>
                <div className="text-brand-subtle mb-1">Facilities</div>
                <ReviewStars value={r_fac} onChange={setRFac} testid="stars-facilities" />
              </div>
            </div>
            <textarea
              data-testid="review-text"
              value={r_text}
              onChange={(e) => setRText(e.target.value)}
              rows={3}
              placeholder="How was the walk for the dog?"
              className="td-input mt-3 resize-none"
            />
            <button
              data-testid="submit-review-btn"
              onClick={submitReview}
              disabled={submittingReview}
              className="td-btn-primary mt-3 w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submittingReview
                ? "Posting…"
                : (user && (reviews.data || []).some((r) => r.user_id === user.user_id)
                    ? "Update your review"
                    : "Post review")}
            </button>
          </div>
        </section>
      </div>
      {reportOpen && (
        <ReportIssueModal
          trailId={id}
          trailName={L0.name}
          onClose={() => setReportOpen(false)}
        />
      )}
      {editOpen && (
        <EditTrailModal
          trail={L0}
          onClose={() => setEditOpen(false)}
        />
      )}
      <ScoutRatingModal
        open={scoutOpen}
        locationId={id}
        locationName={L0.name}
        onClose={() => setScoutOpen(false)}
        onResolved={() => {
          // Trail was promoted out of Scout Needed — invalidate detail so
          // the banner disappears without a page refresh.
          qc.invalidateQueries({ queryKey: ["loc", id] });
        }}
      />
      <AuthGateModal
        open={!!gate}
        onClose={() => setGate(null)}
        reason={
          gate?.kind === "gpx"
            ? "export_gpx"
            : gate?.kind === "walk"
            ? "start_walk"
            : "save_walk"
        }
        title={
          gate?.kind === "gpx"
            ? "Sign in to download GPX files"
            : gate?.kind === "walk"
            ? "Sign in to log your walk"
            : "Create a free account to save this walk"
        }
        body={
          gate?.kind === "gpx"
            ? "GPX downloads are saved to your TrailDog account so they're waiting for you on every device — phone, tablet, GPS watch."
            : gate?.kind === "walk"
            ? "Logged walks live quietly in your profile — a private record of where you and your pack have been. Free, no spam, takes ten seconds."
            : "Sign in once and your saved walks follow you everywhere — phone, tablet, web. Free, no spam, takes ten seconds."
        }
        guestLabel={gate?.kind === "save" ? "Just save it on this device" : undefined}
        onGuest={() => persistAnonFav()}
      />
      {/* Finish-walk confirm */}
      {confirmFinish && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4" onClick={() => setConfirmFinish(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up">
            <h3 className="font-heading text-xl">Finish this walk?</h3>
            <p className="text-sm text-brand-subtle mt-2">We'll log it to your private Walked trails list on Profile.</p>
            <div className="flex gap-2 mt-5">
              <button data-testid="finish-walk-cancel" onClick={() => setConfirmFinish(false)} disabled={submittingWalk} className="td-btn-secondary flex-1 !py-2.5">Not yet</button>
              <button data-testid="finish-walk-confirm" onClick={submitWalk} disabled={submittingWalk} className="td-btn-primary flex-1 !py-2.5 disabled:opacity-60">
                {submittingWalk ? "Saving…" : "Finish walk"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Cancel-walk confirm */}
      {confirmCancel && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4" onClick={() => setConfirmCancel(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up">
            <h3 className="font-heading text-xl">Discard this walk?</h3>
            <p className="text-sm text-brand-subtle mt-2">Fixes recorded so far will be forgotten and nothing is logged.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirmCancel(false)} className="td-btn-secondary flex-1 !py-2.5">Keep walking</button>
              <button data-testid="cancel-walk-confirm" onClick={cancelWalk} className="td-btn-primary flex-1 !py-2.5 !bg-brand-error">Discard</button>
            </div>
          </div>
        </div>
      )}
      {/* Walk logged — finish screen (phase 3). Rich on `completed`
          (quick dog-rating, conditions chips, photo, share, favourites);
          light on `partly_walked` / `unverified` (just outcome + share +
          secondary buttons). Nothing is mandatory — every path has a
          Done. Reuses existing primitives (review API, MudMeterSection,
          ReportIssueModal, PhotoGallery, shareTrail, toggleFav). */}
      {finishedWalk && (
        <WalkFinishSheet
          finishedWalk={finishedWalk}
          onClose={() => setFinishedWalk(null)}
          distanceKm={L0.distance_km}
          reviews={reviews.data || []}
          currentUserId={user?.user_id}
          onQuickRate={async (stars) => {
            // Quick rating from the finish screen captures ONLY the
            // dog_friendliness score. scenery/facilities are intentionally
            // left null so trail averages don't get skewed by a one-tap
            // mirror. The full review form continues to require all three.
            try {
              await api.post(`/locations/${id}/reviews`, {
                dog_friendliness: stars,
                comment: "",
              });
              toast.success("Thanks for rating");
              qc.invalidateQueries({ queryKey: ["loc-reviews", id] });
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't save rating");
            }
          }}
          onOpenMud={() => {
            setFinishedWalk(null);
            setTimeout(() => {
              document.querySelector('[data-testid="mud-meter-anchor"]')
                ?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
          }}
          onOpenHazard={() => {
            setFinishedWalk(null);
            setReportOpen(true);
          }}
          onOpenPhoto={() => {
            setFinishedWalk(null);
            setTimeout(() => {
              const anchor = document.querySelector('[data-testid="photo-gallery-anchor"]');
              anchor?.scrollIntoView({ behavior: "smooth", block: "center" });
              // Nudge the upload button into focus so the native file
              // picker is one tap away.
              setTimeout(() => {
                document.querySelector('[data-testid="upload-photo-btn"]')?.focus?.();
              }, 400);
            }, 50);
          }}
          onShare={() => shareTrail()}
          onSave={() => toggleFav()}
          isFav={fav}
        />
      )}
    </div>
  );
}


// ─── Finish screen (phase 3) ─────────────────────────────────────────
// Rendered when `finishedWalk` is non-null. For `completed` walks it
// offers the full contribution set; for `partly_walked` / `unverified`
// it keeps the lid on and only shows outcome + share + secondary.
function WalkFinishSheet({
  finishedWalk, onClose, distanceKm, reviews, currentUserId,
  onQuickRate, onOpenMud, onOpenHazard, onOpenPhoto, onShare, onSave, isFav,
}) {
  const status = finishedWalk.status || "unverified";
  const pct = Number.isFinite(finishedWalk.coverage_pct) ? finishedWalk.coverage_pct : 0;
  const durationS = finishedWalk.duration_s || 0;
  const minutes = Math.round(durationS / 60);
  const durationLabel = durationS > 0 && durationS < 60 ? "under 1 min" : `${minutes} min`;
  const kmLabel = Number.isFinite(distanceKm) && distanceKm > 0 ? `${distanceKm.toFixed(1)} km` : null;
  const reasonText = {
    no_route: "this trail doesn't have a mapped route yet",
    too_few_fixes: "not enough location points",
    too_fast: "we recorded speeds too fast to be walking",
    low_coverage: "we didn't see enough of the route",
  }[finishedWalk.reason] || "the route couldn't be matched";
  // "Already reviewed?" check — pure read of the reviews list we
  // already fetched for the page. Skips the quick-rating prompt when
  // the walker is a repeat visitor who's rated this trail before.
  const alreadyReviewed = !!currentUserId && (reviews || []).some((r) => r.user_id === currentUserId);
  const [rated, setRated] = React.useState(false);
  const [hoverStars, setHoverStars] = React.useState(0);

  let headline = "Walk logged";
  let subtitle = null;
  if (status === "completed") {
    headline = "Trail complete!";
    subtitle = <>{finishedWalk.trail_name ? `${finishedWalk.trail_name} · ` : ""}<strong>{durationLabel}</strong>{kmLabel ? <> · {kmLabel}</> : null}</>;
  } else if (status === "partly_walked") {
    headline = `You covered about ${pct}% of this trail`;
    subtitle = <>{finishedWalk.trail_name ? `${finishedWalk.trail_name} · ` : ""}<strong>{durationLabel}</strong>{kmLabel ? <> · {kmLabel}</> : null}</>;
  } else {
    headline = "Logged, but we couldn't verify the route";
    subtitle = <>{reasonText.charAt(0).toUpperCase() + reasonText.slice(1)}. <strong>{durationLabel}</strong></>;
  }

  const showContribUI = status === "completed";
  const showRating = showContribUI && !alreadyReviewed && !rated;

  return (
    <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up my-6"
        data-testid="walk-logged-modal"
      >
        <h3 className="font-heading text-2xl" data-testid="walk-logged-headline">{headline}</h3>
        <p className="text-sm text-brand-subtle mt-2">{subtitle}</p>

        {showRating && (
          <div className="mt-5" data-testid="finish-quick-rate">
            <div className="text-sm font-medium text-brand-text mb-2">How was it for your dog?</div>
            <div className="flex items-center gap-1" onMouseLeave={() => setHoverStars(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onMouseEnter={() => setHoverStars(n)}
                  onClick={async () => { setRated(true); await onQuickRate(n); }}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-brand-sand transition-colors"
                  data-testid={`finish-rate-star-${n}`}
                  aria-label={`Rate ${n} out of 5`}
                >
                  <Star
                    size={22}
                    className={n <= hoverStars ? "fill-brand-accent text-brand-accent" : "text-brand-subtle"}
                  />
                </button>
              ))}
            </div>
            <Link
              to={`#reviews-section`}
              onClick={(e) => {
                e.preventDefault();
                onClose();
                setTimeout(() => {
                  document.querySelector('[data-testid="reviews-section"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 50);
              }}
              className="inline-block mt-1 text-xs text-brand-primary underline"
              data-testid="finish-add-more-review"
            >
              Add more
            </Link>
          </div>
        )}

        {showContribUI && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="finish-conditions-chips">
            <button
              onClick={onOpenMud}
              className="td-chip inline-flex items-center gap-1.5 !py-2 !px-3"
              data-testid="finish-chip-mud"
            >
              <Droplets size={14} /> How muddy?
            </button>
            <button
              onClick={onOpenHazard}
              className="td-chip inline-flex items-center gap-1.5 !py-2 !px-3"
              data-testid="finish-chip-hazard"
            >
              <AlertTriangle size={14} /> Spotted a hazard?
            </button>
            <button
              onClick={onOpenPhoto}
              className="td-chip inline-flex items-center gap-1.5 !py-2 !px-3"
              data-testid="finish-chip-photo"
            >
              <Camera size={14} /> Add a photo
            </button>
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <button
            onClick={onShare}
            className="td-btn-primary flex-1 !py-2.5 inline-flex items-center justify-center gap-1.5"
            data-testid="finish-share-btn"
          >
            <Share2 size={15} /> Share this trail
          </button>
        </div>

        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={onSave}
            className="td-btn-secondary flex-1 !py-2 !text-sm inline-flex items-center justify-center gap-1.5"
            data-testid="finish-save-btn"
          >
            <Heart size={14} className={isFav ? "fill-brand-error text-brand-error" : ""} />
            {isFav ? "Saved" : "Save to favourites"}
          </button>
          <Link
            to="/profile"
            className="td-btn-secondary flex-1 !py-2 !text-sm text-center"
            data-testid="finish-view-walks"
          >
            View my walks
          </Link>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <Link
            to="/discover"
            className="td-btn-secondary flex-1 !py-2 !text-sm text-center"
            data-testid="finish-find-nearby"
          >
            Find another walk nearby
          </Link>
          <button
            onClick={onClose}
            className="td-btn-secondary !py-2 !px-4 !text-sm"
            data-testid="finish-done-btn"
          >
            Done
          </button>
        </div>
      </div>
      <HideReviewDialog
        open={!!hideTarget}
        review={hideTarget || {}}
        locationId={id}
        onClose={() => setHideTarget(null)}
        onHidden={() => qc.invalidateQueries({ queryKey: ["loc-reviews", id] })}
      />
    </div>
  );
}


function getEmoji(id) {
  if (!id) return "☀️";
  if (id >= 200 && id < 300) return "⛈️";
  if (id >= 300 && id < 600) return "🌧️";
  if (id >= 600 && id < 700) return "❄️";
  if (id >= 700 && id < 800) return "🌫️";
  if (id === 800) return "☀️";
  return "⛅";
}
